// Game static data - migrated from backend/data_levels.py
// This data doesn't change per user, so it lives in the frontend
// UPDATED: Corrected to match actual n8n documentation (2024-2025)

export const LEVELS = [
  {
    id: 1,
    title: "Bienvenido a n8n",
    description: "Aprende qué es un nodo y cómo funciona el flujo básico",
    difficulty: "beginner",
    concepts_taught: ["node", "workflow", "trigger"],
    required_xp: 0,
    reward_xp: 100,
    tutorial_steps: [
      { step: 1, text: "Un nodo es una unidad de trabajo en n8n que realiza una acción específica", highlight: "node-library" },
      { step: 2, text: "Todo workflow necesita un Trigger para iniciar. Arrastra el Manual Trigger al canvas", highlight: "manual-trigger-node" },
      { step: 3, text: "Ahora arrastra el nodo Edit Fields para establecer datos", highlight: "edit-fields-node" },
      { step: 4, text: "Conecta el Manual Trigger con Edit Fields arrastrando desde el punto de salida", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "edit_fields"],
      required_connections: 1,
      min_nodes: 2
    }
  },
  {
    id: 2,
    title: "El Flujo Básico",
    description: "Crea tu primer flujo de trabajo con transformación de datos",
    difficulty: "beginner",
    concepts_taught: ["workflow", "edit_fields_node", "data_transformation"],
    required_xp: 100,
    reward_xp: 150,
    tutorial_steps: [
      { step: 1, text: "El nodo Edit Fields (Set) permite establecer y modificar valores de datos", highlight: "edit-fields-node" },
      { step: 2, text: "Crea un flujo: Manual Trigger → Edit Fields → HTTP Request", highlight: "canvas" },
      { step: 3, text: "Configura el nodo Edit Fields con campos personalizados", highlight: "node-config" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "edit_fields", "http_request"],
      required_connections: 2,
      min_nodes: 3
    }
  },
  {
    id: 3,
    title: "Decisiones Lógicas",
    description: "Aprende a usar el nodo IF para crear ramas condicionales",
    difficulty: "beginner",
    concepts_taught: ["conditional_logic", "if_node", "branching"],
    required_xp: 250,
    reward_xp: 200,
    tutorial_steps: [
      { step: 1, text: "El nodo IF evalúa condiciones y divide el flujo en dos ramas: True y False", highlight: "if-node" },
      { step: 2, text: "Conecta Manual Trigger → Edit Fields → IF", highlight: "canvas" },
      { step: 3, text: "Configura una condición en el nodo IF", highlight: "if-config" },
      { step: 4, text: "Conecta ambas ramas (True y False) a diferentes nodos de procesamiento", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "if", "edit_fields"],
      required_connections: 3,
      min_nodes: 4,
      has_branching: true
    }
  },
  {
    id: 4,
    title: "Conectando APIs",
    description: "Realiza tu primera petición HTTP a una API externa",
    difficulty: "beginner",
    concepts_taught: ["http_request", "api", "external_data"],
    required_xp: 450,
    reward_xp: 250,
    tutorial_steps: [
      { step: 1, text: "El nodo HTTP Request permite conectar con APIs externas", highlight: "http-request-node" },
      { step: 2, text: "Crea un flujo: Manual Trigger → HTTP Request → Edit Fields", highlight: "canvas" },
      { step: 3, text: "Configura el método HTTP (GET, POST, etc.) y la URL de la API", highlight: "node-config" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "edit_fields"],
      required_connections: 2,
      min_nodes: 3
    }
  },
  {
    id: 5,
    title: "Código Personalizado",
    description: "Ejecuta JavaScript o Python para transformar datos",
    difficulty: "beginner",
    concepts_taught: ["code_node", "javascript", "python", "data_processing"],
    required_xp: 700,
    reward_xp: 300,
    tutorial_steps: [
      { step: 1, text: "El nodo Code permite ejecutar JavaScript o Python personalizado", highlight: "code-node" },
      { step: 2, text: "Puedes elegir entre 'Run Once for All Items' o 'Run Once for Each Item'", highlight: "code-mode" },
      { step: 3, text: "Crea un flujo: Manual Trigger → Edit Fields → Code", highlight: "canvas" },
      { step: 4, text: "Escribe código para procesar y transformar los datos de entrada", highlight: "node-config" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "code", "edit_fields"],
      required_connections: 2,
      min_nodes: 3
    }
  },
  {
    id: 6,
    title: "Integración Completa",
    description: "Crea un flujo completo integrando Edit Fields, IF y HTTP Request",
    difficulty: "intermediate",
    concepts_taught: ["complex_workflow", "data_flow", "integration"],
    required_xp: 1000,
    reward_xp: 350,
    tutorial_steps: [
      { step: 1, text: "Vas a crear un flujo de automatización completo", highlight: "canvas" },
      { step: 2, text: "Usa Edit Fields para preparar datos, IF para validar, y HTTP Request para APIs", highlight: "canvas" },
      { step: 3, text: "Conecta: Manual Trigger → Edit Fields → IF → HTTP Request", highlight: "canvas" },
      { step: 4, text: "Este patrón representa un flujo común: preparar, validar y consumir servicios", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "edit_fields", "if", "http_request"],
      required_connections: 4,
      min_nodes: 5
    }
  },
  {
    id: 7,
    title: "Ramificaciones Múltiples",
    description: "Crea flujos con múltiples condiciones usando Switch",
    difficulty: "intermediate",
    concepts_taught: ["multiple_branches", "switch_node", "complex_logic", "decision_trees"],
    required_xp: 1350,
    reward_xp: 400,
    tutorial_steps: [
      { step: 1, text: "Cuando necesitas más de dos salidas, usa el nodo Switch en lugar de IF", highlight: "switch-node" },
      { step: 2, text: "El nodo Switch permite múltiples rutas de salida basadas en diferentes condiciones", highlight: "canvas" },
      { step: 3, text: "Crea un flujo con lógica de decisión compleja", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "switch", "edit_fields"],
      required_connections: 5,
      min_nodes: 6,
      has_branching: true
    }
  },
  {
    id: 8,
    title: "Transformación de Datos",
    description: "Obtén datos de API y transfórmalos con Code",
    difficulty: "intermediate",
    concepts_taught: ["api_integration", "data_transformation", "json_processing"],
    required_xp: 1750,
    reward_xp: 450,
    tutorial_steps: [
      { step: 1, text: "Combina HTTP Request con Code para transformar respuestas de APIs", highlight: "canvas" },
      { step: 2, text: "Usa el nodo Code para procesar y manipular JSON", highlight: "code-node" },
      { step: 3, text: "Crea: Manual Trigger → HTTP Request → Code → Edit Fields", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "code", "edit_fields"],
      required_connections: 3,
      min_nodes: 4
    }
  },
  {
    id: 9,
    title: "Procesamiento de Listas",
    description: "Divide y agrupa arrays de datos con Split Out y Aggregate",
    difficulty: "intermediate",
    concepts_taught: ["split_out", "aggregate", "batch_processing", "iteration"],
    required_xp: 2200,
    reward_xp: 500,
    tutorial_steps: [
      { step: 1, text: "El nodo Split Out divide un array en items individuales", highlight: "split-out-node" },
      { step: 2, text: "El nodo Aggregate combina múltiples items en uno solo", highlight: "aggregate-node" },
      { step: 3, text: "Usa Loop Over Items (Split in Batches) para procesar en lotes", highlight: "loop-node" },
      { step: 4, text: "Crea un flujo que divida, procese y reagrupe datos", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "split_out", "code", "aggregate"],
      required_connections: 3,
      min_nodes: 4
    }
  },
  {
    id: 10,
    title: "Manejo de Errores",
    description: "Aprende a manejar errores con Error Trigger y configuración de nodos",
    difficulty: "intermediate",
    concepts_taught: ["error_handling", "error_trigger", "fallback", "resilience"],
    required_xp: 2700,
    reward_xp: 550,
    tutorial_steps: [
      { step: 1, text: "Cada nodo tiene opciones de manejo de errores en Settings", highlight: "node-settings" },
      { step: 2, text: "Puedes configurar: Stop Workflow, Continue, o Continue (using error output)", highlight: "error-options" },
      { step: 3, text: "El Error Trigger inicia un workflow cuando otro workflow falla", highlight: "error-trigger" },
      { step: 4, text: "Crea rutas alternativas para manejar fallos de API", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "if", "edit_fields"],
      required_connections: 4,
      min_nodes: 5,
      has_branching: true
    }
  },
  {
    id: 11,
    title: "Webhooks y Triggers",
    description: "Configura un Webhook para recibir datos externos y disparar workflows",
    difficulty: "intermediate",
    concepts_taught: ["webhooks", "triggers", "event_driven", "webhook_node"],
    required_xp: 3250,
    reward_xp: 600,
    tutorial_steps: [
      { step: 1, text: "El nodo Webhook crea un endpoint HTTP que inicia el workflow", highlight: "webhook-node" },
      { step: 2, text: "n8n genera dos URLs: Test URL (para desarrollo) y Production URL", highlight: "webhook-urls" },
      { step: 3, text: "Configura el método HTTP (GET, POST, etc.) y las opciones de respuesta", highlight: "webhook-config" },
      { step: 4, text: "Usa 'Respond to Webhook' para personalizar la respuesta", highlight: "respond-webhook" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["webhook", "edit_fields", "code"],
      required_connections: 2,
      min_nodes: 3
    }
  },
  {
    id: 12,
    title: "Schedule Trigger y Automatización",
    description: "Programa workflows para ejecutarse automáticamente",
    difficulty: "advanced",
    concepts_taught: ["schedule_trigger", "cron", "automation", "scheduled_tasks"],
    required_xp: 3850,
    reward_xp: 650,
    tutorial_steps: [
      { step: 1, text: "El Schedule Trigger ejecuta workflows en horarios definidos", highlight: "schedule-trigger" },
      { step: 2, text: "Puedes configurar intervalos: segundos, minutos, horas, días, semanas, meses", highlight: "schedule-config" },
      { step: 3, text: "También soporta expresiones CRON para programación avanzada", highlight: "cron-expression" },
      { step: 4, text: "Recuerda: debes activar (publish) el workflow para que funcione automáticamente", highlight: "activate-workflow" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["schedule_trigger", "http_request", "code", "edit_fields"],
      required_connections: 3,
      min_nodes: 4
    }
  },
  {
    id: 13,
    title: "Expresiones y Referencias",
    description: "Domina las expresiones para referenciar datos dinámicamente",
    difficulty: "advanced",
    concepts_taught: ["expressions", "variables", "dynamic_data", "data_mapping"],
    required_xp: 4500,
    reward_xp: 700,
    tutorial_steps: [
      { step: 1, text: "Las expresiones permiten referenciar datos de nodos anteriores", highlight: "expressions-editor" },
      { step: 2, text: "Usa {{ $json.campo }} para acceder a datos JSON del nodo anterior", highlight: "json-reference" },
      { step: 3, text: "Usa {{ $node['NombreNodo'].json.campo }} para referenciar nodos específicos", highlight: "node-reference" },
      { step: 4, text: "n8n incluye funciones de transformación integradas para strings, arrays, fechas", highlight: "transformation-functions" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "edit_fields", "code", "if"],
      required_connections: 4,
      min_nodes: 5
    }
  },
  {
    id: 14,
    title: "Merge y Combinación de Datos",
    description: "Combina datos de múltiples fuentes con el nodo Merge",
    difficulty: "advanced",
    concepts_taught: ["merge_node", "data_combination", "join_operations", "data_structures"],
    required_xp: 5200,
    reward_xp: 750,
    tutorial_steps: [
      { step: 1, text: "El nodo Merge combina datos de múltiples ramas del workflow", highlight: "merge-node" },
      { step: 2, text: "Modos disponibles: Append, Combine, Choose Branch, entre otros", highlight: "merge-modes" },
      { step: 3, text: "Usa Compare Datasets para encontrar diferencias entre conjuntos de datos", highlight: "compare-datasets" },
      { step: 4, text: "Crea un flujo que obtenga datos de dos APIs y los combine", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "merge", "edit_fields"],
      required_connections: 4,
      min_nodes: 5
    }
  },
  {
    id: 15,
    title: "Sub-workflows",
    description: "Organiza flujos complejos usando sub-workflows reutilizables",
    difficulty: "advanced",
    concepts_taught: ["subworkflows", "execute_workflow", "modular_design", "reusability"],
    required_xp: 5950,
    reward_xp: 800,
    tutorial_steps: [
      { step: 1, text: "Execute Sub-workflow permite llamar a otro workflow", highlight: "execute-workflow" },
      { step: 2, text: "El sub-workflow debe iniciar con Execute Sub-workflow Trigger", highlight: "workflow-trigger" },
      { step: 3, text: "Puedes pasar datos entre el workflow principal y el sub-workflow", highlight: "data-passing" },
      { step: 4, text: "Esto permite crear flujos modulares y reutilizables", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "execute_workflow", "edit_fields"],
      required_connections: 3,
      min_nodes: 4,
      has_branching: false
    }
  },
  {
    id: 16,
    title: "Integración Multi-API",
    description: "Conecta y coordina múltiples APIs en un solo workflow",
    difficulty: "advanced",
    concepts_taught: ["api_orchestration", "multi_service", "integration", "data_flow"],
    required_xp: 6750,
    reward_xp: 850,
    tutorial_steps: [
      { step: 1, text: "Usa varios nodos HTTP Request para diferentes APIs", highlight: "http-request-node" },
      { step: 2, text: "Coordina las respuestas con Merge y Code", highlight: "merge-code" },
      { step: 3, text: "Maneja errores de cada API de forma independiente", highlight: "error-handling" },
      { step: 4, text: "Crea un flujo que consulte al menos 3 APIs diferentes", highlight: "canvas" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "merge", "code", "edit_fields"],
      required_connections: 5,
      min_nodes: 6
    }
  },
  {
    id: 17,
    title: "Procesamiento de Archivos",
    description: "Lee, procesa y genera archivos con n8n",
    difficulty: "advanced",
    concepts_taught: ["file_processing", "binary_data", "convert_to_file", "extract_from_file"],
    required_xp: 7600,
    reward_xp: 900,
    tutorial_steps: [
      { step: 1, text: "n8n maneja archivos como 'binary data'", highlight: "binary-data" },
      { step: 2, text: "Usa Extract From File para leer CSV, JSON, XML, etc.", highlight: "extract-file" },
      { step: 3, text: "Usa Convert to File para generar archivos desde datos", highlight: "convert-file" },
      { step: 4, text: "Read/Write Files from Disk trabaja con el sistema de archivos local", highlight: "read-write-files" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["manual_trigger", "http_request", "code", "if", "edit_fields"],
      required_connections: 4,
      min_nodes: 5
    }
  },
  {
    id: 18,
    title: "Automatización Compleja",
    description: "Construye un sistema de automatización completo y robusto",
    difficulty: "expert",
    concepts_taught: ["full_automation", "workflow_design", "system_integration", "production_ready"],
    required_xp: 8500,
    reward_xp: 950,
    tutorial_steps: [
      { step: 1, text: "Integra todos los conceptos aprendidos en un workflow completo", highlight: "canvas" },
      { step: 2, text: "Incluye triggers programados, webhooks, y manejo de errores", highlight: "triggers" },
      { step: 3, text: "Usa sub-workflows para organizar la lógica compleja", highlight: "subworkflows" },
      { step: 4, text: "Implementa logging y monitoreo con notificaciones", highlight: "monitoring" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["schedule_trigger", "edit_fields", "http_request", "code", "if", "merge"],
      required_connections: 6,
      min_nodes: 7
    }
  },
  {
    id: 19,
    title: "Proyecto Maestro",
    description: "Demuestra tu maestría creando un flujo completo listo para producción",
    difficulty: "expert",
    concepts_taught: ["mastery", "production_ready", "best_practices", "optimization"],
    required_xp: 9450,
    reward_xp: 1000,
    tutorial_steps: [
      { step: 1, text: "¡Este es el nivel final!", highlight: "canvas" },
      { step: 2, text: "Crea un workflow que integre: triggers, APIs, transformaciones y manejo de errores", highlight: "canvas" },
      { step: 3, text: "Implementa buenas prácticas: nombres descriptivos, organización con sticky notes", highlight: "best-practices" },
      { step: 4, text: "El workflow debe estar listo para activarse en producción", highlight: "production" }
    ],
    initial_node_setup: { nodes: [], connections: [] },
    success_criteria: {
      required_nodes: ["webhook", "edit_fields", "http_request", "code", "if", "merge"],
      required_connections: 7,
      min_nodes: 8,
      has_branching: true
    }
  }
];

export const ACHIEVEMENTS = [
  { achievement_id: "first_step", title: "Primer Paso", description: "Completaste tu primer nivel", icon: "🎯", requirement: "Complete level 1" },
  { achievement_id: "constructor", title: "Constructor", description: "Creaste un flujo con 3 nodos", icon: "🏗️", requirement: "Complete level 2" },
  { achievement_id: "logico", title: "Lógico", description: "Dominaste la lógica condicional", icon: "🧠", requirement: "Complete level 3" },
  { achievement_id: "api_master", title: "Maestro de APIs", description: "Conectaste tu primera API externa", icon: "🌐", requirement: "Complete level 4" },
  { achievement_id: "coder", title: "Programador", description: "Usaste código personalizado", icon: "💻", requirement: "Complete level 5" },
  { achievement_id: "first_steps", title: "Primeros Pasos", description: "Completaste 6 niveles básicos", icon: "🎯", requirement: "Complete 6 levels" },
  { achievement_id: "constructor_milestone", title: "Constructor Experto", description: "Completaste 12 niveles avanzados", icon: "🏗️", requirement: "Complete 12 levels" },
  { achievement_id: "master", title: "Maestro de n8n", description: "Completaste los 19 niveles y dominaste n8n", icon: "👑", requirement: "Complete 19 levels" }
];

export const CONCEPTS = [
  {
    concept_id: "node",
    title: "Nodo",
    description: "Un nodo es una unidad de trabajo que realiza una acción específica en n8n.",
    category: "nodes",
    related_concepts: ["workflow", "trigger"]
  },
  {
    concept_id: "workflow",
    title: "Flujo de Trabajo (Workflow)",
    description: "Un workflow es una secuencia de nodos conectados que automatizan tareas. Siempre comienza con un nodo Trigger.",
    category: "workflows",
    related_concepts: ["node", "trigger", "automation"]
  },
  {
    concept_id: "trigger",
    title: "Trigger (Disparador)",
    description: "Un trigger es el nodo que inicia la ejecución de un workflow. Puede ser manual, programado, webhook, o basado en eventos.",
    category: "nodes",
    related_concepts: ["workflow", "manual_trigger", "schedule_trigger", "webhook"]
  },
  {
    concept_id: "manual_trigger",
    title: "Manual Trigger",
    description: "Inicia el workflow manualmente haciendo clic en 'Execute Workflow'. Útil para desarrollo y pruebas.",
    category: "nodes",
    related_concepts: ["trigger", "workflow"]
  },
  {
    concept_id: "schedule_trigger",
    title: "Schedule Trigger",
    description: "Ejecuta el workflow automáticamente según un horario definido (intervalos o expresiones CRON).",
    category: "nodes",
    related_concepts: ["trigger", "automation", "cron"]
  },
  {
    concept_id: "edit_fields_node",
    title: "Edit Fields (Set)",
    description: "Permite establecer, modificar o eliminar campos de datos. Crucial para preparar datos antes de enviarlos a otros servicios.",
    category: "nodes",
    related_concepts: ["data_transformation", "data_mapping"]
  },
  {
    concept_id: "data_transformation",
    title: "Transformación de Datos",
    description: "Proceso de convertir, modificar o reestructurar datos de un formato a otro.",
    category: "automation",
    related_concepts: ["edit_fields_node", "code_node"]
  },
  {
    concept_id: "conditional_logic",
    title: "Lógica Condicional",
    description: "Permite tomar decisiones y crear ramas en el workflow basadas en condiciones.",
    category: "workflows",
    related_concepts: ["if_node", "switch_node", "branching"]
  },
  {
    concept_id: "if_node",
    title: "Nodo IF",
    description: "Evalúa condiciones y divide el flujo en dos ramas: True (verdadero) y False (falso).",
    category: "nodes",
    related_concepts: ["conditional_logic", "branching", "switch_node"]
  },
  {
    concept_id: "switch_node",
    title: "Nodo Switch",
    description: "Similar al IF pero permite múltiples salidas condicionales. Útil cuando necesitas más de dos ramas.",
    category: "nodes",
    related_concepts: ["conditional_logic", "branching", "if_node"]
  },
  {
    concept_id: "branching",
    title: "Ramificación",
    description: "Crear múltiples caminos de ejecución en un workflow basados en condiciones.",
    category: "workflows",
    related_concepts: ["if_node", "switch_node", "conditional_logic"]
  },
  {
    concept_id: "http_request",
    title: "HTTP Request",
    description: "Realiza solicitudes HTTP a APIs y servicios externos. Soporta todos los métodos HTTP (GET, POST, PUT, DELETE, etc.).",
    category: "nodes",
    related_concepts: ["api", "external_data", "webhook"]
  },
  {
    concept_id: "api",
    title: "API",
    description: "Application Programming Interface - permite la comunicación entre diferentes aplicaciones y servicios.",
    category: "automation",
    related_concepts: ["http_request", "integration", "webhook"]
  },
  {
    concept_id: "external_data",
    title: "Datos Externos",
    description: "Información proveniente de fuentes externas como APIs, webhooks o bases de datos.",
    category: "automation",
    related_concepts: ["http_request", "api"]
  },
  {
    concept_id: "code_node",
    title: "Nodo Code",
    description: "Permite ejecutar código JavaScript o Python personalizado. Soporta modos 'Run Once for All Items' y 'Run Once for Each Item'.",
    category: "nodes",
    related_concepts: ["javascript", "python", "data_processing"]
  },
  {
    concept_id: "javascript",
    title: "JavaScript",
    description: "Lenguaje de programación principal usado en el nodo Code de n8n.",
    category: "automation",
    related_concepts: ["code_node", "python"]
  },
  {
    concept_id: "python",
    title: "Python",
    description: "Lenguaje alternativo soportado en el nodo Code de n8n (requiere Task Runners en versiones recientes).",
    category: "automation",
    related_concepts: ["code_node", "javascript"]
  },
  {
    concept_id: "data_processing",
    title: "Procesamiento de Datos",
    description: "Manipulación y transformación de información dentro del workflow.",
    category: "automation",
    related_concepts: ["code_node", "data_transformation"]
  },
  {
    concept_id: "complex_workflow",
    title: "Flujo Complejo",
    description: "Workflows con múltiples nodos, ramas condicionales y lógica avanzada.",
    category: "workflows",
    related_concepts: ["workflow", "integration", "branching"]
  },
  {
    concept_id: "data_flow",
    title: "Flujo de Datos",
    description: "Cómo los datos se mueven y transforman de un nodo a otro en el workflow.",
    category: "workflows",
    related_concepts: ["workflow", "data_transformation", "expressions"]
  },
  {
    concept_id: "integration",
    title: "Integración",
    description: "Conectar diferentes servicios, aplicaciones y APIs para trabajar juntos.",
    category: "automation",
    related_concepts: ["api", "workflow"]
  },
  {
    concept_id: "multiple_branches",
    title: "Múltiples Ramas",
    description: "Flujos con varias rutas de ejecución condicionales simultáneas.",
    category: "workflows",
    related_concepts: ["branching", "switch_node", "complex_logic"]
  },
  {
    concept_id: "complex_logic",
    title: "Lógica Compleja",
    description: "Combinaciones avanzadas de condiciones, bucles y decisiones en workflows.",
    category: "workflows",
    related_concepts: ["conditional_logic", "decision_trees"]
  },
  {
    concept_id: "decision_trees",
    title: "Árboles de Decisión",
    description: "Estructuras de decisiones anidadas y ramificadas usando múltiples nodos IF o Switch.",
    category: "workflows",
    related_concepts: ["branching", "complex_logic"]
  },
  {
    concept_id: "api_integration",
    title: "Integración de API",
    description: "Conectar y usar APIs externas en workflows de automatización.",
    category: "automation",
    related_concepts: ["api", "http_request"]
  },
  {
    concept_id: "json_processing",
    title: "Procesamiento JSON",
    description: "Trabajar con datos en formato JSON, el formato estándar de datos en n8n.",
    category: "automation",
    related_concepts: ["data_processing", "code_node"]
  },
  {
    concept_id: "split_out",
    title: "Split Out",
    description: "Nodo que divide un array en items individuales para procesarlos uno por uno.",
    category: "nodes",
    related_concepts: ["aggregate", "iteration", "batch_processing"]
  },
  {
    concept_id: "aggregate",
    title: "Aggregate",
    description: "Nodo que combina múltiples items en uno solo, opuesto al Split Out.",
    category: "nodes",
    related_concepts: ["split_out", "batch_processing"]
  },
  {
    concept_id: "loops",
    title: "Bucles",
    description: "Repetir operaciones sobre conjuntos de datos usando Loop Over Items.",
    category: "workflows",
    related_concepts: ["iteration", "batch_processing"]
  },
  {
    concept_id: "iteration",
    title: "Iteración",
    description: "Procesar elementos uno por uno en una colección de datos.",
    category: "workflows",
    related_concepts: ["loops", "split_out"]
  },
  {
    concept_id: "batch_processing",
    title: "Procesamiento en Lote",
    description: "Procesar múltiples elementos en grupos controlados.",
    category: "automation",
    related_concepts: ["loops", "split_out", "aggregate"]
  },
  {
    concept_id: "error_handling",
    title: "Manejo de Errores",
    description: "Gestionar errores y excepciones en workflows. Cada nodo tiene opciones de error en Settings.",
    category: "workflows",
    related_concepts: ["error_trigger", "fallback", "resilience"]
  },
  {
    concept_id: "error_trigger",
    title: "Error Trigger",
    description: "Nodo trigger que inicia un workflow cuando otro workflow falla.",
    category: "nodes",
    related_concepts: ["error_handling", "trigger"]
  },
  {
    concept_id: "fallback",
    title: "Fallback",
    description: "Rutas alternativas que se ejecutan cuando algo falla en el flujo principal.",
    category: "workflows",
    related_concepts: ["error_handling", "resilience"]
  },
  {
    concept_id: "resilience",
    title: "Resiliencia",
    description: "Capacidad de un workflow para manejar fallos y continuar funcionando.",
    category: "workflows",
    related_concepts: ["error_handling", "fallback"]
  },
  {
    concept_id: "webhooks",
    title: "Webhooks",
    description: "Endpoints HTTP que reciben datos de servicios externos y disparan workflows.",
    category: "automation",
    related_concepts: ["webhook_node", "triggers", "event_driven"]
  },
  {
    concept_id: "webhook_node",
    title: "Webhook Node",
    description: "Crea un endpoint HTTP que inicia el workflow cuando recibe una petición.",
    category: "nodes",
    related_concepts: ["webhooks", "http_request", "respond_to_webhook"]
  },
  {
    concept_id: "respond_to_webhook",
    title: "Respond to Webhook",
    description: "Nodo que personaliza la respuesta enviada al cliente que llamó al webhook.",
    category: "nodes",
    related_concepts: ["webhook_node", "webhooks"]
  },
  {
    concept_id: "triggers",
    title: "Triggers",
    description: "Nodos que inician la ejecución de un workflow en respuesta a eventos o condiciones.",
    category: "automation",
    related_concepts: ["manual_trigger", "schedule_trigger", "webhook_node"]
  },
  {
    concept_id: "event_driven",
    title: "Basado en Eventos",
    description: "Workflows que se ejecutan automáticamente en respuesta a eventos externos.",
    category: "automation",
    related_concepts: ["triggers", "webhooks"]
  },
  {
    concept_id: "rest_api",
    title: "API REST",
    description: "Estilo de arquitectura para servicios web basado en recursos y métodos HTTP.",
    category: "automation",
    related_concepts: ["api", "http_request", "crud_operations"]
  },
  {
    concept_id: "crud_operations",
    title: "Operaciones CRUD",
    description: "Create, Read, Update, Delete - las operaciones básicas de gestión de datos.",
    category: "automation",
    related_concepts: ["rest_api", "api"]
  },
  {
    concept_id: "expressions",
    title: "Expresiones",
    description: "Fórmulas dinámicas que referencian datos de otros nodos usando sintaxis {{ }}.",
    category: "automation",
    related_concepts: ["variables", "dynamic_data", "data_mapping"]
  },
  {
    concept_id: "variables",
    title: "Variables",
    description: "Referencias a valores que pueden cambiar durante la ejecución del workflow.",
    category: "automation",
    related_concepts: ["expressions", "dynamic_data"]
  },
  {
    concept_id: "dynamic_data",
    title: "Datos Dinámicos",
    description: "Datos que se obtienen o calculan durante la ejecución del workflow.",
    category: "automation",
    related_concepts: ["expressions", "variables"]
  },
  {
    concept_id: "data_mapping",
    title: "Mapeo de Datos",
    description: "Asociar campos de entrada con campos de salida, transformando la estructura de datos.",
    category: "automation",
    related_concepts: ["edit_fields_node", "expressions", "data_transformation"]
  },
  {
    concept_id: "merge_node",
    title: "Merge Node",
    description: "Combina datos de múltiples ramas del workflow en una sola salida.",
    category: "nodes",
    related_concepts: ["data_combination", "branching"]
  },
  {
    concept_id: "data_combination",
    title: "Combinación de Datos",
    description: "Unir datos de diferentes fuentes o ramas del workflow.",
    category: "automation",
    related_concepts: ["merge_node", "aggregate"]
  },
  {
    concept_id: "subworkflows",
    title: "Sub-workflows",
    description: "Workflows que son llamados desde otros workflows para modularizar la lógica.",
    category: "workflows",
    related_concepts: ["execute_workflow", "modular_design"]
  },
  {
    concept_id: "execute_workflow",
    title: "Execute Sub-workflow",
    description: "Nodo que ejecuta otro workflow y recibe sus resultados.",
    category: "nodes",
    related_concepts: ["subworkflows", "modular_design"]
  },
  {
    concept_id: "modular_design",
    title: "Diseño Modular",
    description: "Organizar workflows en componentes reutilizables y mantenibles.",
    category: "workflows",
    related_concepts: ["subworkflows", "best_practices"]
  },
  {
    concept_id: "binary_data",
    title: "Datos Binarios",
    description: "Archivos como imágenes, PDFs, etc. que n8n maneja de forma especial.",
    category: "automation",
    related_concepts: ["file_processing", "convert_to_file"]
  },
  {
    concept_id: "file_processing",
    title: "Procesamiento de Archivos",
    description: "Leer, modificar y crear archivos dentro de workflows.",
    category: "automation",
    related_concepts: ["binary_data", "extract_from_file", "convert_to_file"]
  },
  {
    concept_id: "extract_from_file",
    title: "Extract From File",
    description: "Nodo que lee contenido de archivos (CSV, JSON, XML, etc.) y lo convierte en datos.",
    category: "nodes",
    related_concepts: ["file_processing", "binary_data"]
  },
  {
    concept_id: "convert_to_file",
    title: "Convert to File",
    description: "Nodo que convierte datos en archivos descargables.",
    category: "nodes",
    related_concepts: ["file_processing", "binary_data"]
  },
  {
    concept_id: "full_automation",
    title: "Automatización Completa",
    description: "Flujos totalmente automatizados que funcionan sin intervención manual.",
    category: "automation",
    related_concepts: ["workflow_design", "production_ready"]
  },
  {
    concept_id: "workflow_design",
    title: "Diseño de Workflows",
    description: "Mejores prácticas para crear workflows eficientes, legibles y mantenibles.",
    category: "workflows",
    related_concepts: ["best_practices", "modular_design"]
  },
  {
    concept_id: "system_integration",
    title: "Integración de Sistemas",
    description: "Conectar diferentes sistemas y plataformas para que trabajen juntos.",
    category: "automation",
    related_concepts: ["api_orchestration", "integration"]
  },
  {
    concept_id: "api_orchestration",
    title: "Orquestación de APIs",
    description: "Coordinar llamadas a múltiples APIs en secuencia o paralelo.",
    category: "automation",
    related_concepts: ["multi_service", "integration"]
  },
  {
    concept_id: "multi_service",
    title: "Múltiples Servicios",
    description: "Integrar varios servicios externos en un solo workflow.",
    category: "automation",
    related_concepts: ["api_orchestration", "integration"]
  },
  {
    concept_id: "mastery",
    title: "Maestría",
    description: "Dominio completo de n8n y sus capacidades de automatización.",
    category: "automation",
    related_concepts: ["production_ready", "best_practices"]
  },
  {
    concept_id: "production_ready",
    title: "Listo para Producción",
    description: "Workflows robustos, probados y confiables para uso en entornos reales.",
    category: "workflows",
    related_concepts: ["mastery", "best_practices", "error_handling"]
  },
  {
    concept_id: "best_practices",
    title: "Mejores Prácticas",
    description: "Estándares y patrones recomendados: nombres descriptivos, manejo de errores, modularidad.",
    category: "workflows",
    related_concepts: ["mastery", "production_ready", "workflow_design"]
  },
  {
    concept_id: "automation",
    title: "Automatización",
    description: "Ejecutar tareas repetitivas sin intervención manual usando n8n.",
    category: "automation",
    related_concepts: ["workflow", "integration", "triggers"]
  },
  {
    concept_id: "cron",
    title: "Expresiones CRON",
    description: "Sintaxis para definir horarios complejos de ejecución (ej: '0 9 * * 1' = lunes a las 9am).",
    category: "automation",
    related_concepts: ["schedule_trigger", "automation"]
  }
];

// UPDATED: Node types now reflect actual n8n core nodes (2024-2025)
export const NODE_TYPES = [
  { type: "manual_trigger", label: "Manual Trigger", color: "#909399", icon: "play", description: "Inicia el workflow manualmente con un clic" },
  { type: "schedule_trigger", label: "Schedule Trigger", color: "#909399", icon: "clock", description: "Ejecuta el workflow según un horario programado" },
  { type: "webhook", label: "Webhook", color: "#909399", icon: "webhook", description: "Inicia el workflow cuando recibe una petición HTTP" },
  { type: "http_request", label: "HTTP Request", color: "#3B82F6", icon: "globe", description: "Realiza solicitudes HTTP a APIs y servicios externos" },
  { type: "edit_fields", label: "Edit Fields (Set)", color: "#10B981", icon: "edit", description: "Establece, modifica o elimina campos de datos" },
  { type: "if", label: "IF", color: "#F59E0B", icon: "git-branch", description: "Divide el flujo en dos ramas según una condición" },
  { type: "switch", label: "Switch", color: "#F59E0B", icon: "git-fork", description: "Divide el flujo en múltiples ramas condicionales" },
  { type: "code", label: "Code", color: "#8B5CF6", icon: "code", description: "Ejecuta código JavaScript o Python personalizado" },
  { type: "merge", label: "Merge", color: "#6366F1", icon: "git-merge", description: "Combina datos de múltiples ramas del workflow" },
  { type: "split_out", label: "Split Out", color: "#6366F1", icon: "split", description: "Divide un array en items individuales" },
  { type: "aggregate", label: "Aggregate", color: "#6366F1", icon: "layers", description: "Combina múltiples items en uno solo" },
  { type: "filter", label: "Filter", color: "#EF4444", icon: "filter", description: "Filtra items basándose en condiciones" },
  { type: "sort", label: "Sort", color: "#EF4444", icon: "sort", description: "Ordena items según criterios especificados" },
  { type: "limit", label: "Limit", color: "#EF4444", icon: "minus", description: "Limita el número de items que pasan" },
  { type: "execute_workflow", label: "Execute Sub-workflow", color: "#34D399", icon: "workflow", description: "Ejecuta otro workflow como sub-proceso" }
];
